import { useLocations } from '@services/common/useLocation'
import { ClipboardList } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { ApplicationDetail } from 'types/api'

import { Button } from '@/components/ui/button'
import { useNavigate } from 'react-router-dom'

import {
  Badge,
  CardLink,
  CardSkeleton,
  DashboardCard,
  EmptyState,
  Row,
  useFormatDate
} from './shared'

interface Props {
  application?: ApplicationDetail
  loading?: boolean
  className?: string
}

export function ApplicationCard({ application, loading, className }: Props) {
  const { t } = useTranslation('userDashboard')
  const navigate = useNavigate()
  const formatDate = useFormatDate()
  const { data: locationTree } = useLocations()

  // Map location id -> name by walking the whole locations tree (any depth).
  const locationMap = useMemo(() => {
    const map = new Map<string, string>()
    const walk = (nodes: unknown) => {
      if (!Array.isArray(nodes)) return
      for (const node of nodes) {
        const n = node as { value?: string; title?: string; children?: unknown }
        if (typeof n.value === 'string' && typeof n.title === 'string') {
          map.set(n.value, n.title)
        }
        if (n.children) walk(n.children)
      }
    }
    walk(locationTree)
    return map
  }, [locationTree])

  const budget =
    application?.rent_min != null || application?.rent_max != null
      ? `${application?.rent_min ?? 0}–${application?.rent_max ?? 0} €`
      : '—'

  const area =
    application?.area_min != null || application?.area_max != null
      ? `${application?.area_min ?? 0}–${application?.area_max ?? 0} m²`
      : '—'

  const locationIds = application?.locations ?? []
  const locationNames = locationIds
    .map((id) => locationMap.get(id))
    .filter((name): name is string => Boolean(name))

  return (
    <DashboardCard
      icon={ClipboardList}
      title={t('application.title')}
      className={className}
      action={
        application ? (
          <CardLink to="/user/application">{t('application.edit')}</CardLink>
        ) : undefined
      }
    >
      {loading ? (
        <CardSkeleton />
      ) : !application ? (
        <EmptyState
          text={t('application.empty')}
          action={
            <Button size="sm" onClick={() => navigate('/user/application')}>
              {t('application.create')}
            </Button>
          }
        />
      ) : (
        <div className="divide-y divide-ink/[0.06]">
          <Row label={t('application.status')}>
            <Badge tone={application.status ? 'teal' : 'gray'}>
              {application.status
                ? t('application.active')
                : t('application.inactive')}
            </Badge>
          </Row>
          <Row label={t('application.budget')}>{budget}</Row>
          <Row label={t('application.area')}>{area}</Row>
          <Row label={t('application.locations')}>
            {locationNames.length
              ? locationNames.slice(0, 3).join(', ') +
                (locationNames.length > 3
                  ? ` +${locationNames.length - 3}`
                  : '')
              : locationIds.length || '—'}
          </Row>
          <Row label={t('application.arrival')}>
            {formatDate(application.arrival_date)}
          </Row>
        </div>
      )}
    </DashboardCard>
  )
}
